import { Popover, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { useParticipant } from '@videosdk.live/react-sdk'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import useIsMobile from '../hooks/useIsMobile'
import useIsTab from '../hooks/useIsTab'
import useWindowSize from '../hooks/useWindowSize'
import MicOffSmallIcon from '../icons/MicOffSmallIcon'
import NetworkIcon from '../icons/NetworkIcon'
import SpeakerIcon from '../icons/SpeakerIcon'
import { getQualityScore, nameTruncated } from '../utils/common'
import * as ReactDOM from 'react-dom'
import { PinIcon, PinOffIcon } from 'lucide-react'
import { useTurns } from '@/contexts/TurnsProvider'
import { TurnType } from '@db8/shared'
import useDarkMode from '@/hooks/useDarkMode'
import { toast } from 'react-toastify'
import { useHall } from '@/contexts/HallProvider'

type CornerDisplayNameProps = {
  participantId: string
  isPresenting?: boolean
  displayName: string
  isLocal?: boolean
  micOn?: boolean
  mouseOver?: boolean
  isActiveSpeaker?: boolean
  isModerator?: boolean
  isUser?: boolean
}

type AVStat = any // SDK stats are not strongly typed in this project

export const CornerDisplayName = ({
  participantId,
  isPresenting = false,
  displayName,
  isLocal = false,
  micOn = false,
  isModerator = false,
  isUser = false,
}: CornerDisplayNameProps) => {
  const isMobile = useIsMobile()
  const isTab = useIsTab()
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 })
  const isXLDesktop = useMediaQuery({ minWidth: 1440 })
  const isDark = useDarkMode()

  const { height: windowHeight } = useWindowSize()

  // we store DOM nodes in these states (callback refs)
  const [statsBoxHeightRef, setStatsBoxHeightRef] = useState<HTMLDivElement | null>(null)
  const [statsBoxWidthRef, setStatsBoxWidthRef] = useState<HTMLDivElement | null>(null)

  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null)
  const { setHallMetadata } = useHall()
  const { pinnedParticipants, currentTurn } = useTurns()

  const statsBoxHeight = useMemo(() => statsBoxHeightRef?.offsetHeight ?? 0, [statsBoxHeightRef])
  const statsBoxWidth = useMemo(() => statsBoxWidthRef?.offsetWidth ?? 0, [statsBoxWidthRef])

  const analyzerSize = isXLDesktop ? 32 : isLGDesktop ? 28 : isTab ? 24 : isMobile ? 20 : 18

  const {
    webcamStream,
    micStream,
    screenShareStream,
    getVideoStats,
    getAudioStats,
    getShareStats,
    getShareAudioStats,
    disableWebcam,
  } = useParticipant(participantId) as any

  const statsIntervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [score, setScore] = useState<number>(100)
  const [audioStats, setAudioStats] = useState<AVStat[]>([])
  const [videoStats, setVideoStats] = useState<AVStat[]>([])
  const badStatStartTimeRef = useRef<number | null>(null)
  const BAD_STAT_DURATION_THRESHOLD = 5000

  const shouldDisableWebcam = useMemo(() => {
    const s = videoStats?.[0]
    if (!s) return false

    const latency = s.rtt || 0
    const jitter = parseFloat(s.jitter) || 0
    const packetLoss = s.totalPackets > 0 ? (s.packetsLost * 100) / s.totalPackets : 0
    const bitrate = parseFloat(s.bitrate) || 0
    const framerate = s?.size?.framerate || 0

    return latency > 800 || jitter > 100 || packetLoss > 8 || bitrate < 100 || framerate < 5
  }, [videoStats])

  const updateStats = async () => {
    let stats: AVStat[] = []
    let aStats: AVStat[] = []
    let vStats: AVStat[] = []

    if (isPresenting) {
      stats = (await getShareStats()) || []
    } else if (webcamStream) {
      stats = (await getVideoStats()) || []
    } else if (micStream) {
      stats = (await getAudioStats()) || []
    }

    if (webcamStream || micStream || isPresenting) {
      vStats = isPresenting ? (await getShareStats()) || [] : (await getVideoStats()) || []
      aStats = isPresenting ? (await getShareAudioStats()) || [] : (await getAudioStats()) || []
    }

    const s = stats && stats.length > 0 ? getQualityScore(stats[0]) : 100

    setScore(s)
    setAudioStats(aStats)
    setVideoStats(vStats)

    // auto-disable webcam if poor for 5s
    if (!isPresenting && webcamStream) {
      const isBad = shouldDisableWebcam
      const now = Date.now()

      if (isBad) {
        if (badStatStartTimeRef.current === null) {
          badStatStartTimeRef.current = now
        } else if (now - badStatStartTimeRef.current >= BAD_STAT_DURATION_THRESHOLD) {
          disableWebcam?.()
          toast('Webcam disabled due to poor network conditions.', {
            position: 'bottom-left',
            autoClose: 4000,
            hideProgressBar: true,
            closeButton: false,
            pauseOnHover: true,
            draggable: true,
            theme: 'light',
          })
          badStatStartTimeRef.current = null
        }
      } else {
        badStatStartTimeRef.current = null
      }
    }
  }

  const qualityStateArray = [
    { label: '', audio: 'Audio', video: 'Video' },
    {
      label: 'Latency',
      audio: audioStats[0]?.rtt ? `${audioStats[0]?.rtt} ms` : '-',
      video: videoStats[0]?.rtt ? `${videoStats[0]?.rtt} ms` : '-',
    },
    {
      label: 'Jitter',
      audio: audioStats[0]?.jitter ? `${parseFloat(audioStats[0]?.jitter).toFixed(2)} ms` : '-',
      video: videoStats[0]?.jitter ? `${parseFloat(videoStats[0]?.jitter).toFixed(2)} ms` : '-',
    },
    {
      label: 'Packet Loss',
      audio:
        audioStats[0]?.totalPackets > 0
          ? `${((audioStats[0]?.packetsLost * 100) / audioStats[0]?.totalPackets).toFixed(2)}%`
          : '-',
      video:
        videoStats[0]?.totalPackets > 0
          ? `${((videoStats[0]?.packetsLost * 100) / videoStats[0]?.totalPackets).toFixed(2)}%`
          : '-',
    },
    {
      label: 'Bitrate',
      audio: audioStats[0]?.bitrate ? `${parseFloat(audioStats[0]?.bitrate).toFixed(2)} kb/s` : '-',
      video: videoStats[0]?.bitrate ? `${parseFloat(videoStats[0]?.bitrate).toFixed(2)} kb/s` : '-',
    },
    {
      label: 'Frame rate',
      audio: '-',
      video: videoStats[0]?.size?.framerate == null ? '-' : `${videoStats[0]?.size?.framerate}`,
    },
    {
      label: 'Resolution',
      audio: '-',
      video:
        videoStats[0]?.size?.width == null
          ? '-'
          : `${videoStats[0]?.size?.width}x${videoStats[0]?.size?.height}`,
    },
    {
      label: 'Codec',
      audio: audioStats[0]?.codec || '-',
      video: videoStats[0]?.codec || '-',
    },
    {
      label: 'Cur. Layers',
      audio: '-',
      video:
        !isLocal && videoStats[0]
          ? `S:${videoStats[0]?.currentSpatialLayer || 0} T:${videoStats[0]?.currentTemporalLayer || 0}`
          : '-',
    },
    {
      label: 'Pref. Layers',
      audio: '-',
      video:
        !isLocal && videoStats[0]
          ? `S:${videoStats[0]?.preferredSpatialLayer || 0} T:${videoStats[0]?.preferredTemporalLayer || 0}`
          : '-',
    },
  ]

  useEffect(() => {
    if (webcamStream || micStream || screenShareStream) {
      updateStats()
      if (statsIntervalIdRef.current) clearInterval(statsIntervalIdRef.current)
      statsIntervalIdRef.current = setInterval(updateStats, 500)
    } else {
      if (statsIntervalIdRef.current) {
        clearInterval(statsIntervalIdRef.current)
        statsIntervalIdRef.current = null
      }
    }
    return () => {
      if (statsIntervalIdRef.current) clearInterval(statsIntervalIdRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webcamStream, micStream, screenShareStream, isPresenting])

  return (
    <>
      <div
        className={`absolute bottom-0 right-0 mb-1 mr-1 rounded-md flex items-end justify-end p-2 border bg-gray-200 dark:bg-background border ${
          !micOn && isUser ? 'border-red-500' : isDark ? 'border-white' : 'border-gray-800'
        }`}
        style={{ zIndex: 10 }}
      >
        {!micOn && !isPresenting ? (
          <MicOffSmallIcon fillcolor={isUser ? 'red' : isDark ? 'white' : '#1f2937'} />
        ) : micOn ? (
          <SpeakerIcon fillcolor={'red'} />
        ) : null}
        <p className="text-sm dark:text-white ml-0.5">
          {isPresenting
            ? isLocal
              ? `You are presenting`
              : `${nameTruncated(displayName, 20)} is presenting`
            : nameTruncated(displayName, 20)}
        </p>
      </div>

      <div className="absolute top-2 left-2 flex w-full justify-between p-2" style={{ zIndex: 11 }}>
        {(webcamStream || micStream || screenShareStream) && isUser ? (
          <div onClick={(e) => e.stopPropagation()} className="rounded-md cursor-pointer">
            <Popover className="relative">
              {({ close }) => (
                <>
                  <Popover.Button
                    className="absolute left-0 top-0 rounded-md flex items-center justify-center p-1.5 cursor-pointer"
                    style={{
                      backgroundColor: score > 7 ? '#3BA55D' : score > 4 ? '#faa713' : '#FF5D5D',
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      const target = e.currentTarget as HTMLElement
                      const rect = target.getBoundingClientRect()
                      setCoords({
                        left: Math.round(rect.x + rect.width / 2),
                        top: Math.round(rect.y + window.scrollY),
                      })
                    }}
                  >
                    <NetworkIcon
                      color1="#ffffff"
                      color2="#ffffff"
                      color3="#ffffff"
                      color4="#ffffff"
                      style={{ height: analyzerSize * 0.6, width: analyzerSize * 0.6 }}
                    />
                  </Popover.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel style={{ zIndex: 999 }} className="absolute">
                      {ReactDOM.createPortal(
                        <div
                          ref={setStatsBoxWidthRef}
                          style={{
                            top:
                              (coords?.top ?? 0) + statsBoxHeight > windowHeight
                                ? windowHeight - statsBoxHeight - 20
                                : (coords?.top ?? 0),
                            left:
                              (coords?.left ?? 0) - statsBoxWidth < 0
                                ? 12
                                : (coords?.left ?? 0) - statsBoxWidth,
                          }}
                          className="absolute"
                        >
                          <div
                            ref={setStatsBoxHeightRef}
                            className="bg-background rounded-lg shadow-lg ring-1 ring-black ring-opacity-5"
                          >
                            <div
                              className="p-[9px] flex items-center justify-between rounded-t-lg"
                              style={{
                                backgroundColor:
                                  score > 7 ? '#3BA55D' : score > 4 ? '#faa713' : '#FF5D5D',
                              }}
                            >
                              <p className="text-sm dark:text-white font-semibold">{`Quality Score : ${
                                score > 7 ? 'Good' : score > 4 ? 'Average' : 'Poor'
                              }`}</p>

                              <button
                                className="cursor-pointer dark:text-white hover:bg-[#ffffff33] rounded-full px-1 text-center"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  close()
                                }}
                              >
                                <XIcon
                                  className="dark:text-white"
                                  style={{ height: 16, width: 16 }}
                                />
                              </button>
                            </div>

                            <div className="flex">
                              <div className="flex flex-col">
                                {qualityStateArray.map((item, index) => (
                                  <div
                                    key={`${item.label}-${index}`}
                                    className="flex"
                                    style={{
                                      borderBottom:
                                        index === qualityStateArray.length - 1
                                          ? ''
                                          : `1px solid #ffffff33`,
                                    }}
                                  >
                                    <div className="flex flex-1 items-center w-[120px]">
                                      {index !== 0 && (
                                        <p className="text-xs dark:text-white my-[6px] ml-2">
                                          {item.label}
                                        </p>
                                      )}
                                    </div>
                                    <div
                                      className="flex flex-1 items-center justify-center"
                                      style={{ borderLeft: `1px solid #ffffff33` }}
                                    >
                                      <p className="text-xs dark:text-white my-[6px] w-[80px] text-center">
                                        {item.audio}
                                      </p>
                                    </div>
                                    <div
                                      className="flex flex-1 items-center justify-center"
                                      style={{ borderLeft: `1px solid #ffffff33` }}
                                    >
                                      <p className="text-xs dark:text-white my-[6px] w-[80px] text-center">
                                        {item.video}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>,
                        document.body,
                      )}
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          </div>
        ) : (
          <div />
        )}

        {isModerator ? (
          <div
            className="cursor-pointer hover:opacity-75 mr-2 text-gray-800 dark:text-gray-100"
            onClick={() => {
              if (pinnedParticipants.includes(participantId)) {
                const cleaned = pinnedParticipants.filter((id) => id !== participantId)
                setHallMetadata({ pinnedParticipants: cleaned } as any)
              } else if (
                !currentTurn ||
                ![TurnType.AffirmativeCrossNegative, TurnType.NegativeCrossAffirmative].includes(
                  currentTurn?.type as any,
                )
              ) {
                setHallMetadata({ pinnedParticipants: [participantId] } as any)
              } else {
                setHallMetadata({
                  pinnedParticipants: [...pinnedParticipants, participantId],
                } as any)
              }
            }}
          >
            {pinnedParticipants.includes(participantId) ? (
              <PinIcon />
            ) : (
              <PinOffIcon className="opacity-25" />
            )}
          </div>
        ) : (
          <div />
        )}
      </div>
    </>
  )
}
